import { useState } from 'react';
import { Box } from '@mui/material';
import { initials } from '@/utils/initials';
import { schemeFor } from '../format';

interface ProfileAvatarProps {
  name: string;
  photoUrl?: string | null;
  accent: string;
  size?: number;
  /** Colored ring in the professional's accent color. */
  ring?: boolean;
}

/** Photo, or a monogram painted with the professional's own brand color. */
export function ProfileAvatar({
  name,
  photoUrl,
  accent,
  size = 56,
  ring = true,
}: ProfileAvatarProps) {
  const [failed, setFailed] = useState(false);
  const scheme = schemeFor(accent).light;
  const showPhoto = photoUrl && !failed;
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '50%',
        p: ring ? `${Math.max(2, Math.round(size / 28))}px` : 0,
        background: ring
          ? `conic-gradient(from 210deg, ${scheme.primary.main}, ${scheme.primary.light}, ${scheme.primary.container}, ${scheme.primary.main})`
          : 'none',
      }}
    >
      <Box
        sx={(theme) => ({
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
          bgcolor: scheme.primary.container,
          color: scheme.primary.onContainer,
          fontWeight: 700,
          fontSize: size * 0.36,
          letterSpacing: '-0.02em',
          border: ring ? `2px solid ${theme.vars.palette.background.paper}` : 'none',
        })}
      >
        {showPhoto ? (
          <Box
            component="img"
            src={photoUrl}
            alt=""
            referrerPolicy="no-referrer"
            onError={() => setFailed(true)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <span aria-hidden>{initials(name)}</span>
        )}
      </Box>
    </Box>
  );
}
